import { Stamp, Receipt, SearchCheck, CalendarClock, Quote } from "lucide-react";

// Plain-English explainer for UCR + Drug & Alcohol Clearinghouse (guide Step/Mission 5).
export default function UcrClearinghouseExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="ucr-clearinghouse-explainer">
      <div className="flex items-center gap-2 mb-3">
        <Stamp className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">UCR &amp; the Clearinghouse</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        Two ongoing compliance items that catch new carriers off guard: <strong>UCR</strong> is an annual fee you pay to
        run interstate, and the <strong>Drug &amp; Alcohol Clearinghouse</strong> is where you check every driver's
        (including your own) testing history before they drive.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Receipt className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">UCR (Unified Carrier Registration)</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            An <strong>annual</strong> registration fee for interstate carriers, based on how many trucks you run. You
            pay it each year at <strong>ucr.gov</strong>. Miss it and a routine roadside inspection can turn into fines
            and an out-of-service order.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <SearchCheck className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Drug &amp; Alcohol Clearinghouse</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            A federal database of CDL drivers' drug &amp; alcohol violations. You must register your company, run a
            <strong> pre-employment query</strong> on every driver — including yourself — and an <strong>annual query</strong>
            {" "}on each after that.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <CalendarClock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          <strong>These are recurring, not one-and-done.</strong> UCR renews every year, and Clearinghouse queries must
          run before anyone drives and every year after. A missed query means you may have let a prohibited driver
          operate — exactly the kind of violation that ends a young carrier's authority.
        </p>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Set a yearly calendar reminder for UCR renewal (it usually opens in the fall for the next year). Even as a
          one-truck operator you must query yourself and enroll in a testing consortium (C/TPA). This isn't legal advice
          — confirm your obligations with FMCSA or your C/TPA.
        </p>
      </div>
    </div>
  );
}
